import React, { useState, useEffect, useCallback } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Input from '@amzn/meridian/input';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Toggle from '@amzn/meridian/toggle';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { getConfig, updateConfig } from '../services/api';
import { CONFIG_TABLE } from '../appConfig';
import CapReleaseConfig from './CapReleaseConfig';
import NodeConfig from './NodeConfig';
import UvpNiBuffer from './UvpNiBuffer';

const RTS_KEYS = [
   { key: 'rts_percentage_sunday', label: 'Sunday' },
   { key: 'rts_percentage_monday', label: 'Monday' },
   { key: 'rts_percentage_default', label: 'Rest of Days' },
];

const CT_TEXT_FIELDS = [
    { key: 'ct_ofd_wape', label: 'OFD WAPE' },
    { key: 'ct_uncap_dilution', label: 'Uncap%dilution' },
    { key: 'ct_vs_utr_threshold', label: 'CT vs UTR Threshold' },
    { key: 'ct_deviation_vs_r1_caps', label: '%DeviationvsR1Caps' },
    { key: 'ct_deviation_final_ni_vs_w2ni', label: 'Deviation%FinalNIvsW2NI' },
    { key: 'ct_final_otr_vs_w2_ni', label: 'FinalOTRvsW2_ni' },
    { key: 'ct_pct_final_otr_vs_w2_ni', label: '%FinalOTRvsW2_ni' },
    { key: 'ct_schedule_increase', label: 'Schedule Increase' },
    { key: 'ct_pct_utr_rollover', label: '%UTR rollover' },
];

const CT_TOGGLES = [
    { key: 'ct_mbe1_ops_shutdown', label: 'MBE1_OpsShutDown' },
    { key: 'ct_mbe2_deviation_vs_r1_caps', label: 'MBE2_DeviationvsR1Caps' },
    { key: 'ct_mbe3_final_ni_vs_w2ni', label: 'MBE3_FinalNIvsW2NI' },
    { key: 'ct_mbe4_incorrect_utr_split', label: 'MBE4_IncorrectUTRSplit' },
    { key: 'ct_mbe5_mech_capacity', label: 'MBE5_MechCapacity' },
    { key: 'ct_mbe6_special_nodes', label: 'MBE6_DOO2,DWJ3,DOO1,DOO4,DAI1,DTK6,DWJ1,XPT' },
    { key: 'ct_mbe7_ct_vs_final_utr', label: 'MBE7_CTvsFinalUTR' },
    { key: 'ct_mbe8_over_schedule', label: 'MBE8_Over_Schedule' },
    { key: 'ct_mbe9_low_otr', label: 'MBE9_Low_OTR' },
];

const Configuration: React.FC = () => {
    const [activeTab, setActiveTab] = useState('node_config');
    const [values, setValues] = useState<Record<string, string>>({});
    const [original, setOriginal] = useState<Record<string, string>>({});
    const [mapping, setMapping] = useState<Record<string, string[]>>({});
    const [originalMapping, setOriginalMapping] = useState<Record<string, string[]>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [newGroup, setNewGroup] = useState('');
    const [newServiceType, setNewServiceType] = useState<Record<string, string>>({});

    const allKeys = [...RTS_KEYS, ...CT_TEXT_FIELDS, ...CT_TOGGLES];

    const loadConfig = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await getConfig(CONFIG_TABLE);
            const items = Array.isArray(result?.items || result) ? (result?.items || result) : [];
            const map: Record<string, string> = {};
            for (const item of items) {
                if (item.config_key) map[item.config_key] = item.config_value || '';
            }
            setValues(map);
            setOriginal(map);

            const stm = map.service_type_mapping;
            const parsed = stm ? JSON.parse(stm) : {};
            setMapping(parsed);
            setOriginalMapping(JSON.parse(JSON.stringify(parsed)));
        } catch (err: any) {
            setError(err.message || 'Failed to load config');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { loadConfig(); }, [loadConfig]);

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const changed = allKeys
                .filter(({ key }) => values[key] !== original[key])
                .map(({ key }) => ({ config_key: key, config_value: values[key] || '' }));
            if (changed.length === 0) { setSuccess('No changes'); setIsSaving(false); return; }
            await updateConfig(CONFIG_TABLE, changed);
            setOriginal({ ...values });
            setSuccess('Configuration saved');
        } catch (err: any) {
            setError(err.message || 'Failed to save');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveMapping = async () => {
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await updateConfig(CONFIG_TABLE, [
                { config_key: 'service_type_mapping', config_value: JSON.stringify(mapping) },
            ]);
            setOriginalMapping(JSON.parse(JSON.stringify(mapping)));
            setSuccess('Service type mapping saved');
        } catch (err: any) {
            setError(err.message || 'Failed to save');
        } finally {
            setIsSaving(false);
        }
    };

    const addServiceType = (group: string) => {
        const val = (newServiceType[group] || '').trim();
        if (!val || mapping[group].includes(val)) return;
        setMapping(prev => ({ ...prev, [group]: [...prev[group], val] }));
        setNewServiceType(prev => ({ ...prev, [group]: '' }));
    };

    const removeServiceType = (group: string, idx: number) => {
        setMapping(prev => ({ ...prev, [group]: prev[group].filter((_, i) => i !== idx) }));
    };

    const addGroup = () => {
        const g = newGroup.trim().toLowerCase();
        if (!g || mapping[g]) return;
        setMapping(prev => ({ ...prev, [g]: [] }));
        setNewGroup('');
    };

    const removeGroup = (group: string) => {
        setMapping(prev => {
            const next = { ...prev };
            delete next[group];
            return next;
        });
    };

    const hasChanges = allKeys.some(({ key }) => values[key] !== original[key]);
    const mappingChanged = JSON.stringify(mapping) !== JSON.stringify(originalMapping);

    if (isLoading) {
        return <Row alignmentHorizontal="center" spacingInset="500"><Loader /></Row>;
    }

    return (
        <Column spacing="400">
            <Text type="h300">System Configuration</Text>

            {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

            <TabGroup value={activeTab} onChange={setActiveTab}>
                <Tab value="node_config">CIT Mapping</Tab>
                <Tab value="rts">RTS Percentage</Tab>
                <Tab value="uvp_buffer">UVP NI Buffer</Tab>
                <Tab value="mapping">Service Type Mapping</Tab>
                <Tab value="ct">Cycle Transfer</Tab>
                <Tab value="cap_release">Cap Release Config</Tab>
            </TabGroup>

            {activeTab === 'rts' && (
                <Column spacing="400">
                    <Column spacing="300" spacingInset="400" backgroundColor="secondary">
                        {RTS_KEYS.map(({ key, label }) => (
                            <Row key={key} widths={['grid-4', 'grid-4']} alignmentVertical="bottom">
                                <Text type="b200">{label}</Text>
                                <Input
                                    value={values[key] || ''}
                                    onChange={(val: string) => setValues(prev => ({ ...prev, [key]: val }))}
                                    type="number"
                                    suffix="%"
                                />
                            </Row>
                        ))}
                    </Column>
                    <Row>
                        <Button type="primary" onClick={handleSave} disabled={!hasChanges || isSaving}>
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                    </Row>
                </Column>
            )}

            {activeTab === 'mapping' && (
                <Column spacing="400">
                    {Object.entries(mapping).map(([group, types]) => (
                        <Column key={group} spacing="300" spacingInset="400" backgroundColor="secondary">
                            <Row alignmentVertical="center" spacing="300">
                                <Text type="h100">{group}</Text>
                                <Button type="tertiary" size="small" onClick={() => removeGroup(group)}>
                                    Remove Group
                                </Button>
                            </Row>
                            <Table headerRows={1} showDividers>
                                <TableRow>
                                    <TableCell>Service Type Name</TableCell>
                                    <TableCell width={80}>Action</TableCell>
                                </TableRow>
                                {types.map((t, idx) => (
                                    <TableRow key={`${group}-${idx}`}>
                                        <TableCell>{t}</TableCell>
                                        <TableCell>
                                            <Button type="tertiary" size="small" onClick={() => removeServiceType(group, idx)}>
                                                ✕
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </Table>
                            <Row spacing="200" alignmentVertical="bottom">
                                <Input
                                    value={newServiceType[group] || ''}
                                    onChange={(val: string) => setNewServiceType(prev => ({ ...prev, [group]: val }))}
                                    placeholder="Add service type..."
                                    size="small"
                                />
                                <Button type="secondary" size="small" onClick={() => addServiceType(group)}>
                                    Add
                                </Button>
                            </Row>
                        </Column>
                    ))}

                    <Row spacing="200" alignmentVertical="bottom">
                        <Input
                            value={newGroup}
                            onChange={setNewGroup}
                            placeholder="New vehicle group name..."
                            size="small"
                        />
                        <Button type="secondary" size="small" onClick={addGroup}>
                            Add Group
                        </Button>
                    </Row>

                    <Row>
                        <Button type="primary" onClick={handleSaveMapping} disabled={!mappingChanged || isSaving}>
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                    </Row>
                </Column>
            )}

            {activeTab === 'ct' && (
                <Column spacing="400">
                    <Column spacing="300" spacingInset="400" backgroundColor="secondary">
                        <Text type="h100">Thresholds</Text>
                        {CT_TEXT_FIELDS.map(({ key, label }) => (
                            <Row key={key} widths={['grid-5', 'grid-4']} alignmentVertical="bottom">
                                <Text type="b200">{label}</Text>
                                <Input
                                    value={values[key] || ''}
                                    onChange={(val: string) => setValues(prev => ({ ...prev, [key]: val }))}
                                    type="number"
                                />
                            </Row>
                        ))}
                    </Column>

                    <Column spacing="300" spacingInset="400" backgroundColor="secondary">
                        <Text type="h100">MBE Flags</Text>
                        {CT_TOGGLES.map(({ key, label }) => (
                            <Row key={key} widths={['grid-8', 'grid-4']} alignmentVertical="center">
                                <Text type="b200">{label}</Text>
                                <Toggle
                                    checked={values[key] === 'true'}
                                    onChange={(checked: boolean) => setValues(prev => ({ ...prev, [key]: String(checked) }))}
                                />
                            </Row>
                        ))}
                    </Column>

                    <Row>
                        <Button type="primary" onClick={handleSave} disabled={!hasChanges || isSaving}>
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                    </Row>
                </Column>
            )}
            {activeTab === 'cap_release' && <CapReleaseConfig />}
            {activeTab === 'node_config' && <NodeConfig />}
            {activeTab === 'uvp_buffer' && <UvpNiBuffer />}
        </Column>
    );
};

export default Configuration;